import React, { useEffect, useRef, useState } from "react";
import {
    isPushSupported,
    getPermissionState,
    getExistingSubscription,
    urlBase64ToUint8Array,
    getVapidPublicKey,
    postSubscription,
    postUnSubscription
} from '../services/pushService';
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, IApplicationState } from "../stores/AppStore";
import { NewsInfo } from "../interfaces/App";
import Button from "./Button";
import { updUser } from "../services/apiService";

function UserNotification() {
    const app = useSelector((appState: IApplicationState) => appState.app);
    const dispatch = useDispatch<AppDispatch>();
    const [supported, setSupported] = useState(false);
    const [permission, setPermission] = useState('default');
    const [subscribed, setSubscribed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [vapidPublicKey, setVapiKey] = useState(null);
    const times = useRef(0);
    const [newsInfo, setNewsInfo] = useState(NewsInfo.none);
    const [newsInfoChanged, setNewsInfoChanged] = useState(false);

    async function refresh() {
        setError('');
        const sup = await isPushSupported();
        setSupported(sup);
        if (!sup) return;

        const perm = await getPermissionState();
        setPermission(perm);

        const sub = await getExistingSubscription();
        setSubscribed(!!sub);
    }
     
    useEffect(() => {
        if (times.current++ === 0) {
            dispatch(getVapidPublicKey(null)).then((res: any) => {
                if (res.type === "get/vapidPublicKey/fulfilled") {
                    setVapiKey(res.payload.data);
                }
            })
            refresh();
        }
    }, []);

    useEffect(() => {
        setNewsInfo(app.user.newsInfo);
    }, [app.user]);

    const saveNewsInfo = () => {
        dispatch(updUser({id: app.user.id, newsInfo: newsInfo})).then(() => {
            setNewsInfoChanged(false);
        });
    }
    const togglePush = async () => {
        if (!('serviceWorker' in navigator)) throw new Error('No serviceWorker support');
        setBusy(true);

        // Wichtig: nimmt die aktive controlling Registration
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();

        if (sub) {
            // UNSUBSCRIBE
            // const endpoint = sub.endpoint;
            await sub.unsubscribe();

            // optional: Server informieren
            dispatch(postUnSubscription({ sub, deleteAllForUser: true }) as any).then(() => {
                sub.unsubscribe();
                refresh();
                setBusy(false);
            });
        } else {
            // SUBSCRIBE
            if (Notification.permission !== 'granted') {
                const perm = await Notification.requestPermission();
                if (perm !== 'granted') return { action: 'permission_denied' };
            }
            const appServerKey = urlBase64ToUint8Array(vapidPublicKey);
            const newSub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: appServerKey
            });

            // Server informieren (attach)
            dispatch(postSubscription(newSub) as any).then(() => {
                setBusy(false);
                refresh();
            });
        }
    }
    
    const renderPush = () => {
        return app.user.newsInfo == NewsInfo.push 
        ? (
            <div>
                <h4>Benachrichtigungen per Push-Nachricht</h4>

                {!supported && (
                    <p>
                        Dieser Browser unterstützt Web Push nicht (oder es ist deaktiviert).
                    </p>
                )}

                {supported && (
                    <>
                        <p>Browser-Permission: <strong>{permission}</strong></p>
                        <p>Status: <strong>{subscribed ? 'aktiv' : 'inaktiv'}</strong></p>

                        {permission === 'denied' && (
                            <p>
                                Push ist im Browser blockiert. Du musst die Website-Berechtigung in den
                                Browser-Einstellungen wieder erlauben.
                            </p>
                        )}

                        <div style={{ display: 'flex', gap: 12 }}>
                            <button onClick={togglePush} disabled={busy || permission === 'denied' || subscribed || app.user.pushEnabled}>
                                Push aktivieren
                            </button>
                            <button onClick={togglePush} disabled={busy || !app.user.pushEnabled}>
                                Push deaktivieren
                            </button>
                        </div>

                        {error && <p style={{ marginTop: 12 }}><strong>Fehler:</strong> {error}</p>}
                        Die Push-Benachrichtigungen sind gerätegebunden. D.h. wenn du hier Push auswählst, 
                        dann gilt das nur für dieses Endgerät (Telefon / PC)
                    </>
                )}
            </div>
        ) : (
            <div>
                Push Notifications
            </div>
        );
    };
    const renderRadioButton = () => {
        return (
            <div>
                <input
                    key={'onInfo'}
                    type="radio"
                    name="newInfo"
                    value={NewsInfo.none}
                    checked={NewsInfo.none == newsInfo}
                    onChange={() => console.log() }
                    onClick={(ev: any) => {setNewsInfoChanged(true); setNewsInfo(ev.target.value);}}
                />
                Keine
                 <input
                    key={'pushInfo'}
                    type="radio"
                    name="newInfo"
                    value={NewsInfo.push}
                    checked={NewsInfo.push == newsInfo}
                    disabled={!supported}
                    onChange={() => console.log() }
                    onClick={(ev: any) => {setNewsInfoChanged(true); setNewsInfo(ev.target.value);}}
                />
                Push
                <input
                    key={"emailInfo"}
                    type="radio"
                    name="newInfo"
                    value={NewsInfo.mail}
                    checked={NewsInfo.mail == newsInfo}
                    disabled={!(app.user.email?.length > 0)}
                    onChange={() => console.log() }
                    onClick={(ev: any) => {setNewsInfoChanged(true); setNewsInfo(ev.target.value);}}
                />
                Mail             
                {/* <input
                    key={"smsInfo"}
                    type="radio"
                    name="newInfo"
                    value={NewsInfo.sms}
                    checked={NewsInfo.sms == newsInfo}
                    disabled={!(app.user.phone?.length > 0)}
                    onChange={() => console.log() }
                    onClick={(ev: any) => {setNewsInfoChanged(true); setNewsInfo(ev.target.value);}}
                />
                SMS              */}
            </div>
        );
    };

    return (
        <div className="dialog_box">
            <div>
                <h3>Benachrichtigungen</h3>
                {renderRadioButton()}
                {newsInfo == NewsInfo.none && <div><h4>Keine Benachrichtigungen</h4> Musst selber immer mal rein schauen.</div>}
                {newsInfo == NewsInfo.push && renderPush()}
                {newsInfo == NewsInfo.mail && <div><h4>Benachrichtigungen per Mail</h4>Mail an: {app.user.email}</div>}
                {newsInfo == NewsInfo.sms  && <div><h4>Benachrichtigungen per SMS</h4>SMS an: {app.user.phone}</div>}
            </div>
            <Button
                onClick={() => saveNewsInfo()}
                label={"Speichern"}
                disabled={!newsInfoChanged}
                className="animButton"
            />
        </div>
    );
}
export default UserNotification;
